import { keepPreviousData } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useQuery } from '@percent/utility'

import { useInsightsContext } from '../context/InsightsContext'
import { FilterValues, MetricResponsePayload } from '../types/insights.types'
import { serialiseFiltersForApi } from '../utils/filterSerialiser'

// 10s stale window + keepPreviousData so a metric keeps its last render visible
// while a new filter combination loads, and retry:1 to ride out one transient
// blip before surfacing an error — matching the conventions of the hooks this
// replaces (useDonationSummary / useDonationCharts).
const METRIC_STALE_TIME_MS = 10_000

export type UseInsightsMetricResult = {
  data: MetricResponsePayload | null
  isLoading: boolean
  /** True only while displaying previous-key data because the new key isn't ready yet. */
  isShowingPlaceholder: boolean
  error: unknown
}

export type UseInsightsMetricOptions = {
  filters?: FilterValues
  /** Top-N limit; only sent when the metric opts into a `limit` query param. */
  limit?: number
  /** Skip the fetch when false (e.g. an optional hero metric that isn't configured). */
  enabled?: boolean
}

export const useInsightsMetric = (
  metric: string,
  { filters = {}, limit, enabled = true }: UseInsightsMetricOptions = {}
): UseInsightsMetricResult => {
  const { domain, service } = useInsightsContext()

  const params = useMemo<Record<string, string | string[] | number>>(() => {
    const base = serialiseFiltersForApi(filters)

    return limit != null ? { ...base, limit } : base
  }, [filters, limit])

  const queryArg = { domain, metric, params }

  const { data, isLoading, isPlaceholderData, error } = useQuery(
    [`insights/${domain}/metrics/${metric}`, queryArg],
    service.getMetric,
    queryArg,
    {
      enabled,
      placeholderData: keepPreviousData,
      staleTime: METRIC_STALE_TIME_MS,
      retry: 1,
    }
  )

  return {
    data: data ?? null,
    isLoading,
    isShowingPlaceholder: isPlaceholderData,
    error,
  }
}
